import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { sftpPgpKeyQuery } from "../../queries";
import { disableSftpPgpKey, generateSftpPgpKey } from "../../services/defs/internal-api";

/**
 * The PGP section is feature-flag gated, so `enabled` keeps the query from firing (and 403-ing) for
 * companies that shouldn't see it.
 */
export const useSftpPgpKey = (enabled = true) => {
	const query = useQuery({ ...sftpPgpKeyQuery(), enabled });
	return { data: query.data, isLoading: query.isLoading, error: query.error };
};

/**
 * Generating supersedes the company's previous key, so the cache is refetched on `onSettled` rather
 * than `onSuccess`: if the server commits but the response never reaches us, refetching is what stops
 * the UI from continuing to offer a public key that is no longer the active one.
 *
 * Error copy goes through `meta.errorMessage` because the shared MutationCache already toasts every
 * mutation error — toasting here as well would stack two.
 */
export const useGenerateSftpPgpKey = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => generateSftpPgpKey(),
		onSettled: async () => {
			await queryClient.invalidateQueries({ queryKey: sftpPgpKeyQuery().queryKey });
		},
		meta: { errorMessage: "Failed to generate PGP key" },
	});
};

export const useDisableSftpPgpKey = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => disableSftpPgpKey(),
		onSettled: async () => {
			await queryClient.invalidateQueries({ queryKey: sftpPgpKeyQuery().queryKey });
		},
		meta: { errorMessage: "Failed to disable PGP key" },
	});
};
